import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, can, canAny } from '../../api/client';
import {
  assignReportProfile,
  deleteReportProfile,
  fetchReportAssignments,
  fetchReportCatalog,
  fetchReportDevices,
  fetchReportProfiles,
  retryReportDevices,
  saveReportProfile,
  unassignReportProfile,
} from '../../api/ops';
import { useSites } from '../../data/hooks';
import { Card, Field, Modal, PresenceBadge } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useMe } from '../auth/session';
import { useReportScope } from '../reports/ReportScope';

const STATUSES: api.ReportSyncStatus[] = ['synced', 'in_progress', 'pending', 'failed', 'not_supported'];
const SYNC_BADGE: Record<api.ReportSyncStatus, string> = {
  synced: 'badge-online',
  in_progress: 'badge-unknown',
  pending: 'badge-warning',
  failed: 'badge-critical',
  not_supported: 'badge-offline',
};
/** Dynamic keys (catalog parameters, server error codes) with a fallback text. */
type Translate = (key: string, opts?: Record<string, unknown>) => string;
const loose = (t: unknown) => t as Translate;

/** Label of a report parameter: the app's translated field name, else the catalog (vendor) name. */
const paramLabel = (t: Translate, p: api.ReportParameter | undefined, key: string) =>
  t(`devices.reads.field.${key}`, { defaultValue: p?.label ?? key }) + (p?.unit ? ` (${p.unit})` : '');

const errorText = (t: Translate, e: unknown) => {
  const code = e instanceof ApiRequestError ? e.code : 'generic';
  return t(`configurationPage.errors.${code}`, {
    defaultValue: t(`errors.${code}`, { defaultValue: t('errors.generic') }),
  });
};

/**
 * Report profiles: which parameters devices put in their periodic reports. A profile is assigned to sites (with their
 * sub-sites) or to devices (one or many at once); Central writes the list to every device whose report differs,
 * including devices that join a site later. A device's own assignment wins over its site's.
 */
export function ReportProfilesTab() {
  const { t } = useTranslation();
  const me = useMe().data;
  const qc = useQueryClient();
  const { clientId } = useReportScope();
  const catalog = useQuery({
    queryKey: ['report-catalog'],
    queryFn: fetchReportCatalog,
    staleTime: 3_600_000,
  });
  const profiles = useQuery({
    queryKey: ['report-profiles', clientId],
    queryFn: () => fetchReportProfiles(clientId),
    enabled: !!clientId,
    refetchInterval: 30_000,
  });
  const [editing, setEditing] = useState<api.ReportProfile | 'new' | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = profiles.data?.items ?? [];
  const selected = items.find((p) => p.id === selectedId) ?? null;
  const protocols = catalog.data?.protocols ?? [];
  const manage = canAny(me, 'device.configure');
  const del = useMutation({
    mutationFn: (id: string) => deleteReportProfile(id),
    onSuccess: (_, id) => {
      if (id === selectedId) setSelectedId(null);
      void qc.invalidateQueries({ queryKey: ['report-profiles'] });
    },
  });
  return (
    <div className="ops-stack">
      <Card
        title={t('configurationPage.reportProfiles')}
        bodyClass=""
        actions={
          manage && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!clientId || !protocols.length}
              onClick={() => setEditing('new')}
            >
              <Plus size={15} /> {t('configurationPage.newProfile')}
            </button>
          )
        }
      >
        <div className="card-body muted small" style={{ paddingBottom: 0 }}>
          {t('configurationPage.hint')}
        </div>
        <div className="ops-scroll">
          <table className="table table-wrap">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('configurationPage.protocol')}</th>
                <th className="right">{t('configurationPage.parameters')}</th>
                <th>{t('configurationPage.assignedTo')}</th>
                <th>{t('configurationPage.devicesStatus')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className={p.id === selectedId ? 'row-selected' : undefined}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                >
                  <td>
                    <strong>{p.name}</strong>
                    {!p.client_id && (
                      <span className="badge badge-unknown" style={{ marginLeft: 6 }}>
                        {t('configurationPage.platformTemplate')}
                      </span>
                    )}
                    {p.description && <div className="muted small">{p.description}</div>}
                  </td>
                  <td>{protocols.find((x) => x.protocol === p.protocol)?.name ?? p.protocol}</td>
                  <td className="right">{p.keys.length}</td>
                  <td className="small">
                    {t('configurationPage.assignedCounts', { sites: p.sites, devices: p.devices })}
                  </td>
                  <td>
                    <SyncCounts sync={p.sync} />
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {p.can_manage && (
                      <>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={t('common.edit')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(p);
                          }}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={t('common.delete')}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(t('configurationPage.confirmDelete', { name: p.name })))
                              del.mutate(p.id);
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={6} className="muted">
                    {profiles.isLoading ? t('common.loading') : t('configurationPage.noProfiles')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {selected && clientId && (
        <>
          <AssignmentsCard profile={selected} clientId={clientId} manage={manage} />
          <DevicesCard
            profile={selected}
            clientId={clientId}
            manage={manage}
            protocol={protocols.find((x) => x.protocol === selected.protocol)}
          />
        </>
      )}
      {editing && clientId && (
        <ProfileEditor
          profile={editing === 'new' ? null : editing}
          clientId={clientId}
          protocols={protocols}
          global={can(me, 'device.configure')}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setEditing(null);
            setSelectedId(id);
            void qc.invalidateQueries({ queryKey: ['report-profiles'] });
          }}
        />
      )}
    </div>
  );
}

function SyncCounts({ sync }: { sync: Record<string, number> }) {
  const { t } = useTranslation();
  const parts = STATUSES.filter((s) => sync[s]);
  if (!parts.length) return <span className="muted small">—</span>;
  return (
    <span className="small" style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {parts.map((s) => (
        <span key={s} className={`badge ${SYNC_BADGE[s]}`}>
          {t(`configurationPage.status.${s}`)} {sync[s]}
        </span>
      ))}
    </span>
  );
}

/** Ordered parameter list editor with the catalog as the source of keys. */
function ProfileEditor({
  profile,
  clientId,
  protocols,
  global,
  onClose,
  onSaved,
}: {
  profile: api.ReportProfile | null;
  clientId: string;
  protocols: api.ReportProtocol[];
  global: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [protocol, setProtocol] = useState(profile?.protocol ?? protocols[0]?.protocol ?? '');
  const proto = protocols.find((p) => p.protocol === protocol);
  const [keys, setKeys] = useState<string[]>(profile?.keys ?? proto?.default_keys ?? []);
  const [listener, setListener] = useState(profile?.listener ?? 0);
  const [platform, setPlatform] = useState(profile ? profile.client_id === null : false);
  const [filter, setFilter] = useState('');
  const byKey = useMemo(() => new Map((proto?.parameters ?? []).map((p) => [p.key, p])), [proto]);
  const max = proto?.max_keys ?? 20;
  const available = (proto?.parameters ?? []).filter(
    (p) =>
      !keys.includes(p.key) &&
      (!filter ||
        `${p.key} ${paramLabel(loose(t), p, p.key)} ${p.group ?? ''}`
          .toLowerCase()
          .includes(filter.toLowerCase())),
  );
  const groups = [...new Set(available.map((p) => p.group ?? ''))];
  const save = useMutation({
    mutationFn: () =>
      saveReportProfile(profile?.id ?? null, {
        client_id: platform ? null : clientId,
        name: name.trim(),
        description: description.trim() || null,
        protocol,
        listener,
        keys,
      }),
    onSuccess: (r) => onSaved(r.id),
  });
  const move = (i: number, d: number) => {
    const n = [...keys];
    [n[i], n[i + d]] = [n[i + d]!, n[i]!];
    setKeys(n);
  };
  return (
    <Modal
      title={profile ? t('configurationPage.editProfile') : t('configurationPage.newProfile')}
      onClose={onClose}
    >
      <Field label={t('common.name')}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label={t('configurationPage.description')}>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="form-grid">
        <Field label={t('configurationPage.protocol')}>
          <select
            className="input"
            value={protocol}
            disabled={!!profile}
            onChange={(e) => {
              setProtocol(e.target.value);
              setKeys(protocols.find((p) => p.protocol === e.target.value)?.default_keys ?? []);
            }}
          >
            {protocols.map((p) => (
              <option key={p.protocol} value={p.protocol}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('configurationPage.listener')} hint={t('configurationPage.listenerHint')}>
          <input
            className="input"
            type="number"
            min={0}
            max={127}
            value={listener}
            onChange={(e) => setListener(Math.min(127, Math.max(0, Number(e.target.value) || 0)))}
          />
        </Field>
      </div>
      {!profile && global && (
        <label className="check-row">
          <input type="checkbox" checked={platform} onChange={(e) => setPlatform(e.target.checked)} />
          {t('configurationPage.platformTemplateHint')}
        </label>
      )}
      <div className="toolbar" style={{ marginTop: 12 }}>
        <strong>{t('configurationPage.reportList', { n: keys.length, max })}</strong>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={() => setKeys(proto?.default_keys ?? [])}>
          <RotateCcw size={14} /> {t('configurationPage.factoryList')}
        </button>
      </div>
      <ol className="param-list">
        {keys.map((k, i) => (
          <li key={k}>
            <span className="ellipsis">
              {paramLabel(loose(t), byKey.get(k), k)} <span className="muted small">{k}</span>
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="icon-btn"
              aria-label={t('configurationPage.moveUp')}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t('configurationPage.moveDown')}
              disabled={i === keys.length - 1}
              onClick={() => move(i, 1)}
            >
              <ArrowDown size={14} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t('common.remove', { defaultValue: 'Remove' })}
              onClick={() => setKeys(keys.filter((x) => x !== k))}
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ol>
      <input
        className="input"
        value={filter}
        aria-label={t('common.search')}
        placeholder={t('common.search')}
        onChange={(e) => setFilter(e.target.value)}
      />
      <Field
        label={t('configurationPage.addParameter')}
        hint={keys.length >= max ? t('configurationPage.full') : undefined}
      >
        <select
          className="input"
          value=""
          disabled={keys.length >= max}
          onChange={(e) => e.target.value && setKeys([...keys, e.target.value])}
        >
          <option value="">{t('configurationPage.chooseParameter', { n: available.length })}</option>
          {groups.map((g) => (
            <optgroup key={g} label={g || '—'}>
              {available
                .filter((p) => (p.group ?? '') === g)
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {paramLabel(loose(t), p, p.key)}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </Field>
      {profile && <p className="muted small">{t('configurationPage.editWarning')}</p>}
      {save.isError && <p className="form-error">{errorText(loose(t), save.error)}</p>}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!name.trim() || !keys.length || keys.length > max || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}

/** Sites (with sub-sites) and single devices holding the profile; adding one moves it from its previous profile. */
function AssignmentsCard({
  profile,
  clientId,
  manage,
}: {
  profile: api.ReportProfile;
  clientId: string;
  manage: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sitesData = useSites().data;
  const assignments = useQuery({
    queryKey: ['report-assignments', profile.id],
    queryFn: () => fetchReportAssignments(profile.id),
  });
  const [site, setSite] = useState('');
  const [picking, setPicking] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['report-assignments', profile.id] });
    void qc.invalidateQueries({ queryKey: ['report-profiles'] });
    void qc.invalidateQueries({ queryKey: ['report-devices'] });
  };
  const assign = useMutation({
    mutationFn: (t: api.ReportProfileTargets) => assignReportProfile(profile.id, t),
    onSuccess: () => {
      setSite('');
      refresh();
    },
  });
  const unassign = useMutation({
    mutationFn: (t: api.ReportProfileTargets) => unassignReportProfile(profile.id, t),
    onSuccess: refresh,
  });
  // the client and every site below it, indented by depth
  const tree = useMemo(() => {
    const sites = sitesData ?? [];
    const out: { id: string; name: string; depth: number }[] = [];
    const walk = (id: string, depth: number) => {
      const s = sites.find((x) => x.id === id);
      if (!s) return;
      out.push({ id, name: s.name, depth });
      sites
        .filter((x) => x.parentId === id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((c) => walk(c.id, depth + 1));
    };
    walk(clientId, 0);
    return out;
  }, [sitesData, clientId]);
  const canEdit = manage;
  return (
    <Card title={t('configurationPage.assignmentsOf', { name: profile.name })}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('configurationPage.assignHint')}
      </p>
      <h4>{t('configurationPage.sites')}</h4>
      <ul className="plain-list">
        {(assignments.data?.sites ?? []).map((s) => (
          <li key={s.site_id}>
            {s.path_names.join(' › ')}
            {canEdit && (
              <button
                type="button"
                className="icon-btn"
                aria-label={t('common.remove', { defaultValue: 'Remove' })}
                onClick={() => unassign.mutate({ site_ids: [s.site_id] })}
              >
                <X size={14} />
              </button>
            )}
          </li>
        ))}
        {!assignments.data?.sites.length && <li className="muted">{t('common.none')}</li>}
      </ul>
      {canEdit && (
        <div className="toolbar">
          <select
            className="input"
            aria-label={t('configurationPage.chooseSite')}
            value={site}
            onChange={(e) => setSite(e.target.value)}
          >
            <option value="">{t('configurationPage.chooseSite')}</option>
            {tree.map((s) => (
              <option key={s.id} value={s.id}>
                {'  '.repeat(s.depth)}
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!site || assign.isPending}
            onClick={() => assign.mutate({ site_ids: [site] })}
          >
            {t('configurationPage.assignSite')}
          </button>
        </div>
      )}
      <h4>{t('configurationPage.devices')}</h4>
      <ul className="plain-list">
        {(assignments.data?.devices ?? []).slice(0, 50).map((d) => (
          <li key={d.device_id}>
            {d.name}
            {canEdit && (
              <button
                type="button"
                className="icon-btn"
                aria-label={t('common.remove', { defaultValue: 'Remove' })}
                onClick={() => unassign.mutate({ device_ids: [d.device_id] })}
              >
                <X size={14} />
              </button>
            )}
          </li>
        ))}
        {(assignments.data?.devices.length ?? 0) > 50 && (
          <li className="muted">
            {t('configurationPage.andMore', { n: (assignments.data?.devices.length ?? 0) - 50 })}
          </li>
        )}
        {!assignments.data?.devices.length && <li className="muted">{t('common.none')}</li>}
      </ul>
      {canEdit && (
        <button type="button" className="btn" onClick={() => setPicking(true)}>
          <Plus size={15} /> {t('configurationPage.assignDevices')}
        </button>
      )}
      {(assign.isError || unassign.isError) && (
        <p className="form-error">{errorText(loose(t), assign.error ?? unassign.error)}</p>
      )}
      {picking && (
        <DevicePicker
          profile={profile}
          clientId={clientId}
          onClose={() => setPicking(false)}
          onPicked={(ids) => assign.mutate({ device_ids: ids }, { onSuccess: () => setPicking(false) })}
          busy={assign.isPending}
        />
      )}
    </Card>
  );
}

/** Devices of the profile's protocol in the client, with their current profile; many can be picked at once. */
function DevicePicker({
  profile,
  clientId,
  onClose,
  onPicked,
  busy,
}: {
  profile: api.ReportProfile;
  clientId: string;
  onClose: () => void;
  onPicked: (ids: string[]) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const list = useQuery({
    queryKey: ['report-picker', clientId, profile.protocol, search],
    queryFn: () =>
      fetchReportDevices({ clientId, protocol: profile.protocol, search: search || null, limit: 2000 }),
  });
  const rows = (list.data?.items ?? []).filter(
    (d) => !(d.profile_id === profile.id && d.source === 'device'),
  );
  return (
    <Modal title={t('configurationPage.assignDevicesTo', { name: profile.name })} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('configurationPage.pickHint')}
      </p>
      <div className="toolbar">
        <input
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          autoFocus
        />
        <button type="button" className="btn" onClick={() => setPicked(rows.map((d) => d.device_id))}>
          {t('configurationPage.selectAll', { n: rows.length })}
        </button>
      </div>
      <div className="ops-scroll" style={{ maxHeight: '50vh' }}>
        <table className="table table-tight table-wrap">
          <tbody>
            {rows.map((d) => (
              <tr key={d.device_id}>
                <td style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    aria-label={d.name}
                    checked={picked.includes(d.device_id)}
                    onChange={(e) =>
                      setPicked(
                        e.target.checked ? [...picked, d.device_id] : picked.filter((x) => x !== d.device_id),
                      )
                    }
                  />
                </td>
                <td>{d.name}</td>
                <td className="muted small">{d.site_name}</td>
                <td className="muted small">{d.profile_name ?? t('configurationPage.unmanaged')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!picked.length || busy}
          onClick={() => onPicked(picked)}
        >
          {t('configurationPage.assignN', { n: picked.length })}
        </button>
      </div>
    </Modal>
  );
}

/** Where every device of the profile stands: written, waiting (offline), failed — with a manual retry. */
function DevicesCard({
  profile,
  clientId,
  manage,
  protocol,
}: {
  profile: api.ReportProfile;
  clientId: string;
  manage: boolean;
  protocol: api.ReportProtocol | undefined;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const list = useQuery({
    queryKey: ['report-devices', clientId, profile.id, status],
    queryFn: () =>
      fetchReportDevices({ clientId, protocol: profile.protocol, profileId: profile.id, status, limit: 500 }),
    refetchInterval: 15_000,
  });
  const retry = useMutation({
    mutationFn: (ids: string[]) => retryReportDevices(ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['report-devices'] }),
  });
  const items = list.data?.items ?? [];
  const failed = items
    .filter((d) => d.status === 'failed' || d.status === 'not_supported')
    .map((d) => d.device_id);
  return (
    <Card
      title={t('configurationPage.devicesOf', { name: profile.name, n: list.data?.total ?? 0 })}
      bodyClass=""
      actions={
        <span className="toolbar" style={{ margin: 0 }}>
          <select
            className="input"
            aria-label={t('configurationPage.statusCol')}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t('common.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`configurationPage.status.${s}`)}
              </option>
            ))}
          </select>
          {manage && failed.length > 0 && (
            <button
              type="button"
              className="btn"
              disabled={retry.isPending}
              onClick={() => retry.mutate(failed)}
            >
              <RotateCcw size={14} /> {t('configurationPage.retryN', { n: failed.length })}
            </button>
          )}
        </span>
      }
    >
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('configurationPage.statusHint', { keys: protocol?.max_keys ?? 20 })}
      </div>
      <div className="ops-scroll">
        <table className="table table-wrap">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('configurationPage.site')}</th>
              <th>{t('configurationPage.presence')}</th>
              <th>{t('configurationPage.source')}</th>
              <th>{t('configurationPage.statusCol')}</th>
              <th>{t('configurationPage.report')}</th>
              <th>{t('configurationPage.syncedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.device_id}>
                <td>{d.name}</td>
                <td className="small">{d.site_name}</td>
                <td>
                  <PresenceBadge presence={(d.presence ?? undefined) as 'online' | undefined} />
                </td>
                <td className="small">{d.source ? t(`configurationPage.from.${d.source}`) : '—'}</td>
                <td>
                  {d.status ? (
                    <span className={`badge ${SYNC_BADGE[d.status]}`}>
                      {t(`configurationPage.status.${d.status}`)}
                    </span>
                  ) : (
                    '—'
                  )}
                  {d.attempts > 0 && d.status !== 'synced' && (
                    <span className="muted small">
                      {' '}
                      · {t('configurationPage.attempts', { n: d.attempts })}
                    </span>
                  )}
                  {d.error && <div className="muted small">{d.error}</div>}
                </td>
                <td className="small">
                  {d.report_matches === null
                    ? t('configurationPage.reportUnknown')
                    : d.report_matches
                      ? t('configurationPage.reportMatches')
                      : t('configurationPage.reportDiffers')}
                </td>
                <td className="small">{d.synced_at ? formatDateTime(d.synced_at) : '—'}</td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={7} className="muted">
                  {list.isLoading ? t('common.loading') : t('configurationPage.noDevices')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(list.data?.total ?? 0) > items.length && (
        <div className="card-body muted small">
          {t('configurationPage.shownOf', { n: items.length, total: list.data?.total ?? 0 })}
        </div>
      )}
    </Card>
  );
}
